// Copyright ©️ 2026 eVolve MEP, LLC

import { type Facility, type Project, useEvolvePost, type WorkOrderPaginated } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { FormModal } from 'components/Mantine/Modals/FormModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { type CartBendId, useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import { BendElementDetailsForm } from 'modules/Shop/EvolveFabrication/BendElementDetails/BendElementDetailsForm';

type BaseProps = {
  onClose: () => void;
  /** Called once every bend in the list has been added to the work order. */
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

const AddCartToWorkOrderForm = ({ onClose, onBendsCreated, ...props }: BaseProps) => {
  const { apiCall: createBend } = useEvolvePost('shop/fabrication/bendCreator');
  const { bends, removeBends } = useBendCart();

  return (
    <BendElementDetailsForm
      onSubmit={async ({ workOrder, ...elementData }) => {
        if (isNil(workOrder)) return;
        const created: CartBendId[] = [];
        try {
          // One at a time, in list order, so the backend increments part names predictably.
          for (const { cartBendId, values } of bends) {
            const { benderProfile, centerBend, ...bendData } = values;
            await createBend({
              workOrderId: workOrder.workOrderId,
              benderProfileId: benderProfile.machineProfileId,
              ...bendData,
              // Center Bend is validated as a maximized leg; see `State/centerBend.ts`.
              maximizeLegLength: bendData.maximizeLegLength || centerBend,
              ...elementData,
            });
            created.push(cartBendId);
          }
        } finally {
          // Bends that were created leave the list even if a later one failed, so a retry doesn't duplicate them.
          // The API hook reports the failure.
          removeBends(created);
        }
        notifications.show({
          title: 'Successfully added bends',
          message: `${created.length} bend${created.length === 1 ? '' : 's'} added to ${workOrder.name}.`,
          color: 'green',
        });
        onBendsCreated();
        onClose();
      }}
      {...props}
    />
  );
};

type Props = BaseProps & { opened: boolean };

/** Collects the work order and element details once, then adds every bend in the bend list to that work order. */
export const AddCartToWorkOrderModal = ({ opened, ...props }: Props) => (
  <FormModal title="Add bends to work order" type="submit" opened={opened} onClose={props.onClose} startDirty>
    {opened && <AddCartToWorkOrderForm {...props} />}
  </FormModal>
);
